import React from 'react';
import {
  Spacer,
  BlockList,
  Typeahead,
  TextBody,
  ResourceCard,
  Icon,
} from '@pypestream/design-system';
import { ResourceLibItem } from '../../../xstate/prostudio.xstate.types';
import { useBobcatFlag } from '../../../hooks';
import { tableStore } from '../../../stores/table-store';
import { LibraryCard } from './library-card';
import { matchesLibrarySearch } from './library-search';
import { SYSTEM_ACTION_ID_PREFIX } from './system-action-nodes';

const isSystem = (item: ResourceLibItem) =>
  !!item.id?.startsWith(SYSTEM_ACTION_ID_PREFIX);

export const ActionNodesList: React.FC<{
  items: ResourceLibItem[];
  disabled?: boolean;
  onSelectItem: (item: ResourceLibItem) => void;
}> = ({ items, disabled, onSelectItem }) => {
  // Project Bobcat: dragging a card onto a Table View row (bobcat-resource-
  // library-drag-drop). Flag off — cards are click-to-add only, unchanged.
  const dragDropAccess = useBobcatFlag('resourceLibraryDragDrop');
  const dragDropEnabled = dragDropAccess === 'allowed' && !disabled;
  const [filteredOptions, setFilteredOptions] =
    React.useState<ResourceLibItem[]>(items);
  const [value, setValue] = React.useState<string>('');

  // Keep the filtered list in sync when the real catalog changes underneath
  // (e.g. resourceLibData finishes loading after the panel is already open).
  React.useEffect(() => {
    setFilteredOptions(items);
  }, [items]);

  const [query, setQuery] = React.useState('');

  if (dragDropAccess === 'allowed') {
    const matches = items.filter((item) => matchesLibrarySearch(item, query));
    return (
      <>
        <div className="c-resource-lib__sticky-search c-resource-lib__sticky-search--action-nodes">
          <input
            type="search"
            className="c-lib-search"
            placeholder="Search commands…"
            aria-label="Search commands"
            data-testid="bobcat-library-action-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {matches.length === 0 ? (
          <p className="c-lib-empty">
            {query.trim()
              ? `No commands match “${query.trim()}”.`
              : 'No action nodes in the catalog yet.'}
          </p>
        ) : (
          // The platform's built-in nodes first, then the catalog's — as the
          // prototype's "System action nodes" / "From your solutions".
          [
            {
              label: 'System action nodes',
              list: matches.filter(isSystem),
              tag: 'System',
            },
            {
              label: 'From your solutions',
              list: matches.filter((i) => !isSystem(i)),
              tag: undefined,
            },
          ]
            .filter((g) => g.list.length > 0)
            .map((g) => (
              <React.Fragment key={g.label}>
                <span className="c-lib-group">{g.label}</span>
                <div className="c-lib-list">
                  {g.list.map((item) => (
                    <LibraryCard
                      key={item.id}
                      item={item}
                      kind="action"
                      tag={g.tag}
                      disabled={disabled}
                      dragEnabled={dragDropEnabled}
                      onSelect={onSelectItem}
                    />
                  ))}
                </div>
              </React.Fragment>
            ))
        )}
      </>
    );
  }

  const handleTypeheadInput = (event: CustomEvent) => {
    setFilteredOptions(event.detail.filtered);
    setValue(event.detail.value);
  };

  return (
    <>
      <div className="c-resource-lib__sticky-search c-resource-lib__sticky-search--action-nodes">
        <Typeahead
          searchKeys={['name']}
          data={items}
          onInput={handleTypeheadInput}
          value={value}
        />
      </div>
      <Spacer />
      <BlockList>
        {filteredOptions.map((item) => (
          <div
            key={item.id}
            draggable={dragDropEnabled}
            onDragStart={
              dragDropEnabled
                ? (e) => {
                    if (!item.data) {
                      e.preventDefault();
                      return;
                    }
                    e.dataTransfer.effectAllowed = 'copy';
                    e.dataTransfer.setData('text/plain', item.name ?? '');
                    tableStore.setResourceLibDragData({
                      data: item.data,
                      name: item.name ?? 'Resource',
                    });
                  }
                : undefined
            }
            onDragEnd={
              dragDropEnabled
                ? () => tableStore.setResourceLibDragData(null)
                : undefined
            }
          >
            <ResourceCard
              interactive
              variant="neutral"
              disabled={disabled}
              onClick={() => onSelectItem(item)}
              label={item.name}
              description={item.description}
            >
              <Icon name="action-node" size="small" slot="label-prefix" />
              <Icon
                name="plus"
                size="small"
                slot="hover-action"
                color="purple"
                variant="filled"
                weight="medium"
              />
            </ResourceCard>
          </div>
        ))}
      </BlockList>
      {filteredOptions.length === 0 ? (
        <TextBody variant="secondary" textAlign="center" size="small">
          No results
        </TextBody>
      ) : null}
    </>
  );
};
